import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Drawer,
  Hidden,
  SvgIcon,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { withStyles, useStyles } from 'tss-react/mui';
import PopConfirm from 'src/components/PopConfirm';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DoDisturbAltIcon from '@mui/icons-material/DoDisturbAlt';
import helpers from 'src/helpers';
import { PERMISSIONS } from 'src/constants';

import bulkOperationsStyle from 'src/views/management/PartyListView/BulkOperations/bulkOperations.style';

function BulkOperations({
  className,
  onClickRemoveMessages,
  open,
  selected,
  classes,
  userRolePermissions,
  scheduledMessages = [],
}) {
  const { cx } = useStyles();

  const canDeleteMessages = helpers.permissions.doesUserHavePermission(PERMISSIONS.PERMISSIONS_SYSTEM_CODE.SCHEDULED_MESSAGES_EDIT, userRolePermissions);

  const {
    selectedMessagesAreSending,
    selectedMessagesAreNotSending,
    selectedMessagesAreUndefined,
  } = useMemo(() => {
    const newSelected = {
      selectedMessagesAreSending: [],
      selectedMessagesAreNotSending: [],
      selectedMessagesAreUndefined: [],
    };

    if (!selected || !selected?.length) {
      return { ...newSelected };
    }

    if (!scheduledMessages || !scheduledMessages.length) {
      return {
        ...newSelected,
        selectedMessagesAreUndefined: [...selected],
      };
    }

    const messages = selected.map(({ id, type }) => {
      const current = id && type && scheduledMessages.find((message) => message?.id === id && message?.messageType === type);

      return {
        id,
        type,
        isSending: current?.isSending,
      };
    });

    return {
      ...newSelected,
      selectedMessagesAreSending: messages.filter((item) => !!item?.isSending),
      selectedMessagesAreNotSending: messages.filter((item) => !item?.isSending),
    };
  }, [
    selected,
    scheduledMessages
  ]);

  const handleClickRemoveMessages = () => {
    const stopSending = selectedMessagesAreSending?.length && !selectedMessagesAreNotSending?.length;
    onClickRemoveMessages(stopSending);
  };

  const removeMessagesConfirmText = useMemo(() => {
    const textForAction = selectedMessagesAreSending?.length && selectedMessagesAreNotSending?.length ? 'remove or stop sending' : selectedMessagesAreSending?.length ? 'stop sending' : 'remove';

    return selected?.length === 1 ? `Are you sure you want to ${textForAction} this connection?` : `Are you sure you want to ${textForAction} these connections?`;
  }, [
    selected,
    selectedMessagesAreSending,
    selectedMessagesAreNotSending,
  ]);

  const removeMessagesText = useMemo(() => {
    const textForAction = selectedMessagesAreSending?.length && selectedMessagesAreNotSending?.length ? 'REMOVE OR STOP SENDING' : selectedMessagesAreSending?.length ? 'STOP SENDING' : 'REMOVE SCHEDULED';

    return selected?.length === 1 ? `${textForAction} CONNECTION` : `${textForAction} CONNECTIONS`;
  }, [
    selected,
    selectedMessagesAreSending,
    selectedMessagesAreNotSending,
  ]);

  return (
    <Drawer
      anchor="bottom"
      open={open}
      PaperProps={{ elevation: 1 }}
      variant="persistent"
    >
      <div
        className={cx(classes.root, className)}
      >
        <Grid
          alignItems="center"
          container
          spacing={2}
        >
          <Hidden mdDown>
            <Grid md={3}>
              <Typography
                color="textSecondary"
                variant="subtitle1"
              >
                {selectedMessagesAreUndefined?.length ? 'Selected messages are undefined' : `${selected.length} selected`}
              </Typography>
            </Grid>
          </Hidden>
          {selected?.length ? (
            <Grid
              md={6}
              xs={12}
            >
              <div className={classes.actions}>
                <PopConfirm
                  title={removeMessagesConfirmText}
                  onConfirm={handleClickRemoveMessages}
                >
                  <Button
                    disabled={!canDeleteMessages || selectedMessagesAreUndefined?.length}
                  >
                    <SvgIcon
                      fontSize="small"
                      className={classes.actionIcon}
                      component={selectedMessagesAreSending?.length && !selectedMessagesAreNotSending?.length ? DoDisturbAltIcon : DeleteOutlineIcon}
                    />
                    {removeMessagesText}
                  </Button>
                </PopConfirm>
              </div>
            </Grid>
          ) : (
            <Grid
              md={6}
              xs={12}
            />
          )}
        </Grid>
      </div>
    </Drawer>
  );
}

BulkOperations.propTypes = {
  classes: PropTypes.object.isRequired,
  selected: PropTypes.array.isRequired,
  className: PropTypes.string,
  onClickRemoveMessages: PropTypes.func,
  open: PropTypes.bool,
  userRolePermissions: PropTypes.array,
  scheduledMessages: PropTypes.array,
};

BulkOperations.defaultProps = {
  className: '',
  onClickRemoveMessages: () => { },
  open: false,
  userRolePermissions: [],
};

export default withStyles(BulkOperations, bulkOperationsStyle, { withTheme: true });
